import { Button } from "@chakra-ui/react";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import type { ClientEntity } from "../types/ClientEntity";
import { useGetClientEntities } from "../useCases/useGetClientEntities";
import { useCreateClientEntity } from "../useCases/useCreateClientEntity";
import { useUpdateClientEntity } from "../useCases/useUpdateClientEntities";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { LuPlus } from "react-icons/lu";
import { Pagination } from "@/shared/core-configs";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { ClientEntitiesTableColumns } from "../Columns";
import { useAuth } from "@/shared/session";
import {
  ClientEntityFormInput,
  CreateClientEntityModal,
} from "./CreateClientEntityModal";
import { UpdateClientEntityModal } from "./UpdateClientEntityModal";
import { toast } from "@/ui/chakra/toaster";
import { PageContainer } from "@/ui/containers/PageContainer";

export const ClientEntitiesTable = () => {
  const { session } = useAuth();
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });
  const [showDisabled, setShowDisabled] = useState(false);

  const [selectedClientEntity, setSelectedClientEntity] =
    useState<ClientEntity | null>(null);

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState<string>();

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: clientEntities = [],
    refetch,
    isPending,
    pagination: metaClientEntity,
  } = useGetClientEntities({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled,
    searchTerm: debouncedSearchTerm,
    ...order,
    zoneIds: session?.zone ?? undefined,
  });
  const { mutate: createClientEntity } = useCreateClientEntity();
  const { mutate: updateClientEntity } = useUpdateClientEntity();

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  const handleOpenEditModal = (clientEntity: ClientEntity) => {
    setSelectedClientEntity(clientEntity);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedClientEntity(null);
  };

  const handleCreateClientEntity = (data: ClientEntityFormInput) => {
    createClientEntity(data, {
      onSuccess: () => {
        setCreateModalOpen(false);
        notify.success("Empresa creada");
        refetch();
      },
      onError: (error) => notify.error("No se pudo crear la empresa", error),
    });
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (clientEntity: ClientEntity) => {
      const ok = await confirmToggle(
        clientEntity.enabled,
        `la empresa ${clientEntity.name}`,
      );
      if (!ok) return;
      const updatedClientEntity: ClientEntityFormInput = {
        name: clientEntity.name,
        description: clientEntity.description,
        zones: clientEntity.zones.map((zone) => zone.id),
        enabled: !clientEntity.enabled,
      };

      updateClientEntity(
        { id: clientEntity.id ?? "", updatedClientEntity },
        {
          onSuccess: () => {
            notify.success(
              clientEntity.enabled
                ? "Empresa deshabilitada"
                : "Empresa habilitada",
            );
            refetch();
          },
          onError: (error) =>
            notify.error("No se pudo actualizar la empresa", error),
        },
      );
    },
    [updateClientEntity, refetch, confirmToggle],
  );

  const clientEntitiesTableColumns = useMemo(
    () => ClientEntitiesTableColumns(handleToggleEnabled),
    [handleToggleEnabled],
  );

  const handleEditClientEntity = (item: ClientEntity) => {
    if (item.zones.length > 1) {
      toast({
        title: "Error al editar empresa cliente",
        description: "No se puede editar",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    handleOpenEditModal(item);
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm]);

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Empresas clientes"
      description="Crea y administra las empresas clientes según las necesidades."
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear empresa cliente
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por nombre…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        emptyDescription="Las empresas cliente se asignan a las órdenes de trabajo y a los vales."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear empresa cliente
          </Button>
        }
        headers={clientEntitiesTableColumns}
        data={clientEntities}
        extractItemKey={(item: ClientEntity) => item.id}
        onEdit={(item: ClientEntity) => {
          handleEditClientEntity(item);
        }}
        pages={metaClientEntity?.totalPages}
        totalItems={metaClientEntity?.count}
        itemsLabel="empresas"
        showDisabledCheck
        onSortChange={(sort, order) => {
          setOrder({ sort, order });
        }}
        onShowDisabledChange={(showDisabled) => setShowDisabled(showDisabled)}
        onSearch={(searchTerm) => {
          setSearchTerm(searchTerm);
        }}
        onChangePage={(page) => {
          setPagination({ ...pagination, page: page + 1 });
        }}
        hiddenColumnsKey="clientEntities"
        isLoading={isPending}
      />

      <CreateClientEntityModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateClientEntity}
      />

      {selectedClientEntity && (
        <UpdateClientEntityModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          selectedClientEntity={selectedClientEntity}
        />
      )}
    </PageContainer>
  );
};
